import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Clock, ArrowLeft, TrendUp, TrendDown, Lightning, ShareNetwork, TwitterLogo, Copy, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API, trackEvent } from "@/App";
import SEO from "@/components/SEO";
import { useIsAppNavVisible } from "@/hooks/useIsAppNavVisible";
import PublicNav from "@/components/PublicNav";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { ToolCTA } from "@/components/InlineCTA";
import { PlayerCard, PlayerCardGrid } from "@/components/PlayerCard";
import { PlayerPhoto } from "@/components/PlayerPhoto";

// Resolve player names to Sleeper IDs for photos
async function resolvePlayerIds(names) {
  const map = {};
  for (const name of names) {
    try {
      const res = await axios.get(`${API}/players/search?q=${encodeURIComponent(name)}&numQbs=2&numTeams=12&ppr=1`);
      const match = res.data?.find(p => p.name?.toLowerCase() === name.toLowerCase());
      if (match) map[name] = { sleeperId: match.sleeperId, value: match.value, position: match.position, team: match.team };
    } catch {}
  }
  return map;
}

function RichContent({ content, playerMap }) {
  if (!content) return null;

  // Split content into paragraphs
  const paragraphs = content.split("\n\n").filter(p => p.trim());

  return (
    <div className="space-y-4">
      {paragraphs.map((para, i) => {
        const trimmed = para.trim();

        // Section headers
        if (trimmed.startsWith("**Buy Low**") || trimmed.startsWith("**Risers**")) {
          return (
            <div key={i} className="flex items-center gap-2 mt-8 mb-2">
              <TrendUp size={18} weight="bold" className="text-[#4ecdc4]" />
              <h2 className="font-heading text-xl text-[#4ecdc4] uppercase">
                {trimmed.includes("Buy Low") ? "BUY LOW" : "RISERS"}
              </h2>
            </div>
          );
        }
        if (trimmed.startsWith("**Sell High**") || trimmed.startsWith("**Fallers**")) {
          return (
            <div key={i} className="flex items-center gap-2 mt-8 mb-2">
              <TrendDown size={18} weight="bold" className="text-[#ff6b6b]" />
              <h2 className="font-heading text-xl text-[#ff6b6b] uppercase">
                {trimmed.includes("Sell High") ? "SELL HIGH" : "FALLERS"}
              </h2>
            </div>
          );
        }

        // Player mention paragraphs — detect **Name (POS, TEAM)** pattern
        const playerMatch = trimmed.match(/^\*\*([^*]+)\s*\(([A-Z]{2}),\s*([A-Z]{2,3})\)\*\*/);
        if (playerMatch) {
          const [, pName, pos, team] = playerMatch;
          const pInfo = playerMap[pName.trim()];
          const restOfText = trimmed.replace(/^\*\*[^*]+\*\*\s*—?\s*/, "");

          // Determine if buy or sell based on surrounding context
          const isBuy = content.substring(0, content.indexOf(trimmed)).includes("Buy Low") && !content.substring(0, content.indexOf(trimmed)).includes("Sell High");

          return (
            <div key={i} className="border border-[#262626] bg-[#171717] rounded-sm overflow-hidden">
              <div className="flex items-center gap-3 p-3 border-b border-[#262626]">
                <PlayerPhoto playerId={pInfo?.sleeperId} name={pName.trim()} position={pos} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-white">{pName.trim()}</span>
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${isBuy ? "bg-[#4ecdc4]/15 text-[#4ecdc4]" : "bg-[#ff6b6b]/15 text-[#ff6b6b]"}`}>
                      {isBuy ? "BUY" : "SELL"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#888]">{pos} · {team}</span>
                    {pInfo?.value > 0 && <span className="text-[10px] font-mono text-[#ccff00]">{pInfo.value.toLocaleString()}</span>}
                  </div>
                </div>
              </div>
              <p className="text-sm text-[#a3a3a3] leading-relaxed p-3">{restOfText}</p>
            </div>
          );
        }

        // Regular paragraphs with **bold** rendering
        return (
          <p key={i} className="text-sm text-[#a3a3a3] leading-relaxed">
            {trimmed.split("**").map((part, j) =>
              j % 2 === 0 ? part : <strong key={j} className="text-white">{part}</strong>
            )}
          </p>
        );
      })}
    </div>
  );
}

function ShareBar({ title, slug }) {
  const [copied, setCopied] = useState(false);
  const url = `https://dynastytrademaker.com/blog/${slug}`;
  const text = encodeURIComponent(title);
  const encodedUrl = encodeURIComponent(url);

  const copyLink = () => {
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-8 mb-4 flex items-center gap-3" data-testid="share-bar">
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#555]">SHARE</span>
      <a href={`https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-8 h-8 border border-[#333] rounded-sm text-[#888] hover:border-[#1DA1F2] hover:text-[#1DA1F2] transition-colors no-underline" title="Share on X/Twitter" data-testid="share-twitter">
        <TwitterLogo size={14} weight="bold" />
      </a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-8 h-8 border border-[#333] rounded-sm text-[#888] hover:border-[#4267B2] hover:text-[#4267B2] transition-colors no-underline" title="Share on Facebook" data-testid="share-facebook">
        <ShareNetwork size={14} weight="bold" />
      </a>
      <button onClick={copyLink} className="flex items-center justify-center w-8 h-8 border border-[#333] rounded-sm text-[#888] hover:border-[#ccff00] hover:text-[#ccff00] transition-colors" title="Copy link" data-testid="share-copy">
        {copied ? <Check size={14} weight="bold" className="text-[#4ade80]" /> : <Copy size={14} weight="bold" />}
      </button>
    </div>
  );
}

export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then(r => setPosts(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const BLOG_HEROES = {
    "Weekly Column": "/hero-images/blog-buy-low-sell-high.jpg",
    "Rookie Watch": "/hero-images/blog-rookie-watch.jpg",
    "Trade Analysis": "/hero-images/blog-trade-of-the-week.jpg",
    "Draft": "/hero-images/guide-rookie-draft.jpg",
    "Devy": "/hero-images/blog-rookie-watch.jpg",
    "Education": "/hero-images/guide-startup-draft.jpg",
  };

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO page="home" title="Dynasty Trading Blog — Dynasty Trade Maker" description="Dynasty fantasy football strategy, trade tips, player analysis, and platform updates from Dynasty Trade Maker." path="/blog" />
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00]">THE DYNASTY PLAYBOOK</span>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mt-2 mb-3" data-testid="blog-title">BLOG</h1>
          <p className="text-base text-[#a3a3a3]">Strategy, analysis, and tips to dominate your dynasty leagues.</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i => <div key={i} className="h-64 skeleton rounded-sm" />)}</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#262626]"><p className="text-[#a3a3a3] text-sm">No posts yet. Check back soon!</p></div>
        ) : (
          <>
            {/* Featured Post */}
            {featured && (
              <Link to={`/blog/${featured.slug}`} className="group block border border-[#262626] bg-[#171717] rounded-sm overflow-hidden no-underline hover:border-[#ccff00]/30 transition-colors mb-8" data-testid={`blog-featured-${featured.slug}`}>
                <div className="aspect-[2/1] sm:aspect-[3/1] overflow-hidden">
                  <img src={BLOG_HEROES[featured.category] || "/hero-images/blog-buy-low-sell-high.jpg"} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="eager" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#ccff00] bg-[#ccff00]/10 px-2 py-0.5 rounded-sm">{featured.category}</span>
                    <span className="text-[10px] text-[#666]">{new Date(featured.created_at).toLocaleDateString()}</span>
                  </div>
                  <h2 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight mb-2 group-hover:text-[#ccff00] transition-colors">{featured.title}</h2>
                  <p className="text-sm text-[#a3a3a3] leading-relaxed mb-3">{featured.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-[#ccff00]">READ ARTICLE <ArrowRight size={12} weight="bold" /></span>
                </div>
              </Link>
            )}

            {/* Post Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="blog-posts">
              {rest.map(post => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="group border border-[#262626] bg-[#171717] rounded-sm overflow-hidden no-underline hover:border-[#ccff00]/30 transition-colors" data-testid={`blog-post-${post.slug}`}>
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={BLOG_HEROES[post.category] || "/hero-images/blog-buy-low-sell-high.jpg"} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[7px] font-bold uppercase tracking-wider text-[#ccff00] bg-[#ccff00]/10 px-1.5 py-0.5 rounded-sm">{post.category}</span>
                      <span className="text-[9px] text-[#555]">{new Date(post.created_at).toLocaleDateString()}</span>
                    </div>
                    <h3 className="text-sm text-white font-semibold mb-1 group-hover:text-[#ccff00] transition-colors leading-tight">{post.title}</h3>
                    <p className="text-[11px] text-[#666] leading-relaxed line-clamp-2">{post.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        <NewsletterSignup source="blog" className="mt-8" />
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playerMap, setPlayerMap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/posts/${slug}`);
        setPost(res.data);

        // Resolve player IDs for photos
        const content = res.data?.content || "";
        const names = [];
        const regex = /\*\*([A-Z][a-z]+ [A-Z][a-z'-]+(?:\s[A-Z][a-z'-]+)?)\s*\(/g;
        let match;
        while ((match = regex.exec(content)) !== null) names.push(match[1].trim());

        // Also add players from the structured data
        if (res.data?.players) {
          for (const list of [res.data.players.buy_low, res.data.players.sell_high, res.data.players.risers, res.data.players.fallers]) {
            if (list) list.forEach(p => { if (p.name && !names.includes(p.name)) names.push(p.name); });
          }
        }

        if (names.length > 0) {
          const map = await resolvePlayerIds([...new Set(names)]);
          setPlayerMap(map);
        }
      } catch {}
      setLoading(false);
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title={post ? `${post.title} — Dynasty Trade Maker Blog` : `Blog — Dynasty Trade Maker`} description={post?.excerpt || "Dynasty fantasy football blog."} path={`/blog/${slug}`} image={(() => {
        const heroes = {"Weekly Column":"https://dynastytrademaker.com/hero-images/blog-buy-low-sell-high.jpg","Rookie Watch":"https://dynastytrademaker.com/hero-images/blog-rookie-watch.jpg","Trade Analysis":"https://dynastytrademaker.com/hero-images/blog-trade-of-the-week.jpg","Draft":"https://dynastytrademaker.com/hero-images/guide-rookie-draft.jpg","Devy":"https://dynastytrademaker.com/hero-images/blog-rookie-watch.jpg","Education":"https://dynastytrademaker.com/hero-images/guide-startup-draft.jpg"};
        return post?.og_image || (post?.category && heroes[post.category]) || undefined;
      })()} />
      <PublicNav />
      <div className="max-w-3xl mx-auto px-4 py-12">
        {loading ? (
          <div className="space-y-4"><div className="h-8 w-2/3 skeleton rounded-sm" /><div className="h-64 skeleton rounded-sm" /></div>
        ) : !post ? (
          <div className="text-center py-16">
            <p className="text-[#a3a3a3] text-lg mb-4">Post not found</p>
            <Link to="/blog" className="text-[#ccff00] text-sm no-underline hover:underline">Back to blog</Link>
          </div>
        ) : (
          <>
            <Link to="/blog" className="inline-flex items-center gap-1 text-xs text-[#666] hover:text-[#a3a3a3] no-underline mb-6">
              <ArrowLeft size={12} /> BACK TO BLOG
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#ccff00] bg-[#ccff00]/10 px-2 py-0.5">{post.category}</span>
              <span className="text-[10px] text-[#666]">{new Date(post.created_at).toLocaleDateString()}</span>
              <span className="text-[10px] text-[#666]">By {post.author}</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase tracking-tight mb-4" data-testid="blog-post-title">{post.title}</h1>

            {/* Hero Image */}
            {(() => {
              const heroes = {"Weekly Column":"/hero-images/blog-buy-low-sell-high.jpg","Rookie Watch":"/hero-images/blog-rookie-watch.jpg","Trade Analysis":"/hero-images/blog-trade-of-the-week.jpg","Draft":"/hero-images/guide-rookie-draft.jpg","Devy":"/hero-images/blog-rookie-watch.jpg","Education":"/hero-images/guide-startup-draft.jpg"};
              const src = heroes[post.category];
              return src ? <div className="mb-6 border border-[#262626] rounded-sm overflow-hidden"><img src={src} alt="" className="w-full h-auto" loading="eager" /></div> : null;
            })()}

            {/* Structured player cards for buy-low/sell-high posts */}
            {post.players?.buy_low?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <TrendUp size={16} weight="bold" className="text-[#4ecdc4]" />
                  <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#4ecdc4]">BUY LOW TARGETS</h2>
                </div>
                <PlayerCardGrid
                  players={post.players.buy_low.map(p => ({ ...p, sleeperId: playerMap[p.name]?.sleeperId }))}
                  verdict="BUY"
                />
              </div>
            )}
            {post.players?.sell_high?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <TrendDown size={16} weight="bold" className="text-[#ff6b6b]" />
                  <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#ff6b6b]">SELL HIGH TARGETS</h2>
                </div>
                <PlayerCardGrid
                  players={post.players.sell_high.map(p => ({ ...p, sleeperId: playerMap[p.name]?.sleeperId }))}
                  verdict="SELL"
                />
              </div>
            )}
            {post.players?.risers?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <TrendUp size={16} weight="bold" className="text-[#4ecdc4]" />
                  <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#4ecdc4]">TOP RISERS</h2>
                </div>
                <PlayerCardGrid
                  players={post.players.risers.map(p => ({ ...p, sleeperId: playerMap[p.name]?.sleeperId }))}
                  verdict="BUY"
                />
              </div>
            )}
            {post.players?.fallers?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <TrendDown size={16} weight="bold" className="text-[#ff6b6b]" />
                  <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#ff6b6b]">TOP FALLERS</h2>
                </div>
                <PlayerCardGrid
                  players={post.players.fallers.map(p => ({ ...p, sleeperId: playerMap[p.name]?.sleeperId }))}
                  verdict="SELL"
                />
              </div>
            )}

            {/* Rich content with inline player mentions */}
            <RichContent content={post.content} playerMap={playerMap} />

            {/* Mid-article contextual CTA */}
            {post.category === "Weekly Column" && (
              <ToolCTA text="Before you trade on these picks — check the values" subtext="Real-time dynasty values for every player, updated live." buttonText="OPEN CALCULATOR" buttonLink="/trade-calculator" />
            )}
            {post.category === "Rookie Watch" && (
              <ToolCTA text="Buying or selling a rookie? Check the pick value" subtext="Every rookie pick priced in real-time." buttonText="VALUE ANY PICK" buttonLink="/trade-calculator" />
            )}
            {post.category === "Trade Analysis" && (
              <ToolCTA text="Find trades like these in your own league" subtext="AI scans every team and finds balanced deals for your roster." buttonText="TRY TRADE FINDER" buttonLink="/pricing" />
            )}

            {/* Share Buttons */}
            <ShareBar title={post.title} slug={slug} />

            {/* Trade calculator CTA */}
            <div className="mt-10 border border-[#ccff00]/30 bg-[#ccff00]/5 p-5 sm:p-6 text-center rounded-sm">
              <h3 className="font-heading text-lg text-white uppercase mb-2">ACT ON THESE PICKS</h3>
              <p className="text-xs text-[#888] mb-4">Check current values and make the trade before the market corrects.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/trade-calculator" className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#b8e600] transition-colors">
                  <Lightning size={12} weight="fill" /> FREE TRADE CALCULATOR
                </Link>
                <Link to="/rankings" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">Live Value Chart <ArrowRight size={11} /></Link>
              </div>
            </div>

            {/* Newsletter */}
            <NewsletterSignup source={`blog-${slug}`} className="mt-6" />
          </>
        )}
      </div>
    </div>
  );
}
